import type { Choice } from "./content";

export type ItemResponseFormInputType =
	| "text"
	| "select"
	| "number"
	| "date"
	| "time"
	| "datetime"
	| "email"
	| "currency";

export type ItemResponseFormField = {
	id: string;
	label: string;
	placeholder: string;
	helperText: string;
	inputType: ItemResponseFormInputType;
	options?: Choice[];
	expected_response?: string;
	expectedResponse?: string[];
	required?: boolean;
};

export type WordSelectionIncorrectWord = {
	id: string;
	wordIndex: number;
	transcriptWord: string;
	audioWord: string;
};

export type DragDropBlank = {
	id: string;
};

export type ItemCriteria = {
	id?: string;
	name: string;
	description?: string;
	points?: {
		min: number;
		max: number;
	};
	icon?: string;
	maxPoints?: number;
};

export type TextResponseConstraints = {
	minLength?: number;
	maxLength?: number;
};

export type WordLimit = {
	min?: number;
	max?: number;
};

export type RecordingResponseConstraints = {
	minDuration: number;
	maxDuration: number;
};

// Either plain preparation seconds (item timers start after it elapses) or
// split audio/microphone delays; the two shapes never coexist.
export type ResponsePrepTime =
	| number
	| {
			audioDelaySeconds?: number;
			microphoneDelaySeconds?: number;
	  };

export type SelectionResponseConstraints = {
	minSelected?: number;
	maxSelected?: number;
};

export type ItemResponse = (
	| {
			type: "text";
			label?: string;
			placeholder?: string;
			constraints?: TextResponseConstraints;
			instruction?: string;
			wordLimit?: WordLimit;
	  }
	| {
			type: "form";
			fields: ItemResponseFormField[];
			instruction?: string;
	  }
	| {
			type: "audio";
			instruction?: string;
	  }
	| {
			type: "recording";
			instruction?: string;
			maxDuration?: number;
	  }
	| {
			type: "choice";
			choices: Choice[];
			shuffle?: boolean;
			constraints?: SelectionResponseConstraints;
			points?: number;
			selectionType?: "single" | "multiple";
			instruction?: string;
			maxCorrectChoices?: number;
			minCorrectChoices?: number;
			correctResponse?: { id: string; order?: number; text?: string }[];
	  }
	| {
			type: "reorder";
			choices?: Choice[];
			instruction?: string;
			correctResponse?: { id: string; order?: number; text?: string }[];
	  }
	| {
			type: "RECORDED_AUDIO";
			instruction?: string;
	  }
	| {
			type: "EXTENDED_TEXT";
			instruction?: string;
			wordLimit?: WordLimit;
	  }
	| {
			type: "SHORT_TEXT_ENTRY";
			instruction?: string;
			wordLimit?: WordLimit;
	  }
	| {
			type: "writing";
			instruction?: string;
			wordLimit?: WordLimit;
	  }
	| {
			type: "OPTION_SELECTION";
			choices: Choice[];
			shuffle?: boolean | null;
			instruction?: string;
	  }
	| {
			type: "INLINE_OPTION_SELECTION";
			instruction?: string;
			blanks: {
				blankId: string;
				choices: Choice[];
			}[];
	  }
	| {
			type: "BLANK_DROPDOWN";
			instruction?: string;
			blanks: {
				blankId: string;
				choices: Choice[];
			}[];
	  }
	| {
			type: "TOKEN_MAPPING";
			instruction?: string;
			wordBank: Choice[];
	  }
	| {
			type: "PAIR_MATCHING";
			instruction?: string;
			leftLabel?: string;
			rightLabel?: string;
			left: Choice[];
			right: Choice[];
	  }
	| {
			type: "INLINE_TEXT_SELECTION";
			instruction?: string;
			incorrectWords?: WordSelectionIncorrectWord[];
	  }
	| {
			type: "ORDER_RECONSTRUCTION";
			instruction?: string;
	  }
	| {
			type: "ACKNOWLEDGE";
			timeLimitSeconds?: number;
			instruction?: string;
	  }
) & {
	itemPoints?: number;
	totalPoints?: number;
	config?: ResponseConfig;
};

export type VideoPlayerConfig = {
	allowVideoPlayPause?: boolean;
	allowVideoVolumeControl?: boolean;
	showVideoProgress?: boolean;
	allowVideoCaptions?: boolean;
	allowVideoFullscreen?: boolean;
};

export type ResponseConfig = {
	prepTime?: ResponsePrepTime;
	timeLimit?: number;
	autoPlayAudio?: boolean;
	autoPlayChildAudio?: boolean;
	allowAnswerBeforeAudioCompletion?: boolean;
	autoSkipOnTimeout?: boolean;
	allowNextBeforeTimeout?: boolean;
	allowAudioReplay?: boolean;
	allowPrevNavigation?: boolean;
	itemTimerStartTrigger?:
		| "ON_ITEM_LOAD"
		| "AFTER_AUDIO_COMPLETION"
		| "AFTER_VIDEO_COMPLETION";
	overrideTestTimerWithItemTimer?: boolean;
	autoPlayVideo?: boolean;
	allowVideoReplay?: boolean;
	videoPlayerConfig?: VideoPlayerConfig;
};
